import type { Badge, LessonResult, UserProfile } from '../types';
import { getTrin, getTier } from '../data/curriculum';
import {
  allTrinIdsInTier,
  getCheckpointAfterTier,
  getNextTrin,
  hasPassedTrinProof,
  trinLabel,
} from '../lib/curriculum';
import { PROGRESSION } from '../lib/featureFlags';
import {
  bestRound,
  isTrinCleared,
  lessonSummaryMessage,
  modeLabel,
  recommend,
  topSlowKeys,
  topWeakKeys,
  xpForLesson,
} from '../lib/stats';
import { useEnterAction } from '../lib/useEnterAction';
import {
  DICTATION_TRIN_ID,
  STORY_TRIN_ID,
  STORY2_TRIN_ID,
  isExtraTrinId,
  BONUS_EMAIL_TRIN_ID,
  BONUS_TAL_TRIN_ID,
  METRONOME_TRIN_ID,
  SHADOW_TRIN_ID,
  WEAK_SPOTS_TRIN_ID,
  PUNCTUATION_PRACTICE_TRIN_ID,
  CUSTOM_PRACTICE_TRIN_ID,
  MENTAL_IMAGERY_TRIN_ID,
} from '../lib/extraRounds';
import { WARMUP_TRIN_ID } from '../lib/warmup';
import { FLOW_PRACTICE_TRIN_ID } from '../lib/flowPractice';
import {
  formatSurvivalEndDetail,
  formatSurvivalMs,
  metronomeSurvivalRecordMs,
} from '../lib/metronomeSurvival';
import MentalImagerySummary from './MentalImagerySummary';
import { useCallback } from 'react';

interface LessonSummaryProps {
  result: LessonResult;
  user: UserProfile;
  newBadges: Badge[];
  recordBeat?: boolean;
  survivalRecordBeat?: boolean;
  proofFailed?: boolean;
  onRepeat: () => void;
  onRetryProof?: () => void;
  onAdvance: () => void;
  onBack: () => void;
}

export default function LessonSummary({
  result,
  user,
  newBadges,
  recordBeat = false,
  survivalRecordBeat = false,
  proofFailed = false,
  onRepeat,
  onRetryProof,
  onAdvance,
  onBack,
}: LessonSummaryProps) {
  const isWarmup = result.trinId === WARMUP_TRIN_ID;
  const isFlowPractice = result.trinId === FLOW_PRACTICE_TRIN_ID;
  const isExtra = isExtraTrinId(result.trinId);
  const trin = getTrin(result.trinId);
  const rec = recommend(result);
  const cleared = isTrinCleared(result);
  const proofPending =
    !isExtra &&
    PROGRESSION.trinProof &&
    PROGRESSION.requireTrinProof &&
    cleared &&
    !hasPassedTrinProof(user, result.trinId);
  const xpEarned = xpForLesson(result);
  const weakest = topWeakKeys(result.errorsByKey, 1)[0];
  const slowest = topSlowKeys(result.responseTimes, 1, 3)[0];
  const best = bestRound(result.rounds);
  const survivalRound = result.rounds.find(
    (r) => r.metronomeMode === 'survival' && r.survivalMs != null,
  );
  const nextTrin = isExtra ? null : getNextTrin(result.trinId);
  const canAdvance =
    !isExtra &&
    rec.kind === 'advance' &&
    cleared &&
    nextTrin !== null &&
    !proofPending &&
    !proofFailed;
  const checkpointAfter = isExtra
    ? undefined
    : getCheckpointAfterTier(result.tierId);
  const tierTrinsComplete =
    !isExtra &&
    cleared &&
    checkpointAfter !== undefined &&
    allTrinIdsInTier(result.tierId).every(
      (id) =>
        user.completedTrinIds.includes(id) ||
        (cleared && id === result.trinId),
    );

  const onEnterPrimary = useCallback(() => {
    if (proofFailed && onRetryProof) onRetryProof();
    else if (canAdvance) onAdvance();
    else onRepeat();
  }, [proofFailed, onRetryProof, canAdvance, onAdvance, onRepeat]);

  useEnterAction(onEnterPrimary);

  if (result.trinId === MENTAL_IMAGERY_TRIN_ID) {
    return (
      <MentalImagerySummary
        result={result}
        onRepeat={onRepeat}
        onBack={onBack}
      />
    );
  }

  const banner = (() => {
    if (isExtra) {
      return {
        cls: 'bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/30',
        text: isWarmup
          ? 'God varm-op. Det gamle stof er stadig der.'
          : isFlowPractice
            ? 'Godt skrivestræk — rolig øvelse uden test.'
            : result.trinId === STORY_TRIN_ID
            ? 'Historien er færdig — godt gået med valg og diktat.'
            : result.trinId === STORY2_TRIN_ID
              ? 'Din historie er færdig — godt gået med afskrift og egne ord.'
              : 'Pænt arbejde — den slags ekstra øvelse hjælper rytmen.',
      };
    }
    switch (rec.kind) {
      case 'advance':
        return {
          cls: 'bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/30',
          text: rec.reason,
        };
      case 'repeat':
        return {
          cls: 'bg-rose-100 text-rose-800 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-200 dark:ring-rose-500/30',
          text: rec.reason,
        };
      case 'continue':
        return {
          cls: 'bg-indigo-100 text-indigo-800 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-200 dark:ring-indigo-500/30',
          text: rec.reason,
        };
    }
  })();

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header className="text-center">
        <p className="label">
          {isExtra ? 'Øvelse afsluttet' : 'Lektion afsluttet'}
        </p>
        <h1 className="mt-1 text-3xl font-semibold text-strong">
          {isExtra
            ? extraTitle(result.trinId)
            : trin
              ? trinLabel(trin)
              : result.trinId}
        </h1>
      </header>

      {recordBeat && (
        <div className="rounded-2xl bg-amber-100 p-4 text-center ring-1 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/30">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            Ny personlig rekord!
          </p>
          <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
            Bedre WPM eller nøjagtighed end tidligere på dette trin.
          </p>
        </div>
      )}

      {survivalRecordBeat && (
        <div className="rounded-2xl bg-amber-100 p-4 text-center ring-1 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/30">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            Ny overlevelses-rekord!
          </p>
          <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
            Du holdt længere uden fejl end tidligere.
          </p>
        </div>
      )}

      {survivalRound?.survivalMs != null && (
        <div className="rounded-2xl bg-indigo-50 p-4 text-center ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:ring-indigo-500/30">
          <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">
            Overlevet {formatSurvivalMs(survivalRound.survivalMs)}
          </p>
          <p className="mt-1 text-xs text-indigo-800/80 dark:text-indigo-200/80">
            {formatSurvivalEndDetail(survivalRound)}
            {metronomeSurvivalRecordMs(user) > 0 &&
              ` Rekord: ${formatSurvivalMs(metronomeSurvivalRecordMs(user))}.`}
          </p>
        </div>
      )}

      {proofFailed && (
        <div className="rounded-2xl bg-rose-100 p-4 text-center ring-1 ring-rose-200 dark:bg-rose-500/10 dark:ring-rose-500/30">
          <p className="text-sm font-semibold text-rose-900 dark:text-rose-200">
            Trin-bevis ikke bestået
          </p>
          <p className="mt-1 text-xs text-rose-800/80 dark:text-rose-200/80">
            Lektionen er klaret, men du skal bestå trin-beviset for at gå videre
            i progressionen.
          </p>
        </div>
      )}

      {proofPending && !proofFailed && (
        <div className="rounded-2xl bg-amber-100 p-4 text-center ring-1 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/30">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            Afslut trin-bevis
          </p>
          <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
            Du har klaret lektionen — tag det korte trin-bevis for at låse trinnet
            op.
          </p>
        </div>
      )}

      <div className={`rounded-2xl p-4 text-center ring-1 ${banner.cls}`}>
        <p className="text-sm">{banner.text}</p>
        <p className="mt-2 text-xs opacity-80">
          {lessonSummaryMessage(result)}
        </p>
        {cleared && canAdvance && (
          <p className="mt-2 text-xs opacity-80">
            Dit aktive trin er opdateret — du kan starte næste trin med det samme.
          </p>
        )}
        {tierTrinsComplete && (
          <p className="mt-2 text-xs opacity-80">
            Tip: Der venter en hurtig test mellem niveauerne på oversigten — helt
            valgfrit.
          </p>
        )}
      </div>

      <section className="grid grid-cols-3 gap-3">
        <BigStat label="WPM" value={result.wpm.toFixed(0)} />
        <BigStat
          label="Nøjagtighed"
          value={`${(result.accuracy * 100).toFixed(1)}%`}
        />
        <BigStat label="Varighed" value={formatDuration(result.durationMs)} />
        <BigStat
          label={isFlowPractice ? 'Tegn skrevet' : 'Tegn'}
          value={String(
            isFlowPractice ? result.correctChars : result.totalKeystrokes,
          )}
        />
        <BigStat label="Fejl" value={String(result.mistakes)} />
        <BigStat
          label="Runder fuldført"
          value={`${result.rounds.filter((r) => r.completed).length} / ${result.rounds.length}`}
        />
      </section>

      <section className="grid grid-cols-2 gap-3">
        <InsightCard
          label="Bedste runde"
          primary={best ? best.title : '—'}
          secondary={
            best
              ? `${(best.accuracy * 100).toFixed(0)}% · ${best.wpm.toFixed(0)} wpm · ${modeLabel(best.mode)}`
              : ''
          }
        />
        <InsightCard
          label={isExtra ? 'Øvelse' : 'Klar til næste trin?'}
          primary={
            isExtra
              ? `${(result.accuracy * 100).toFixed(0)}% nøjagtighed`
              : proofPending || proofFailed
                ? 'Afventer bevis'
                : cleared
                  ? 'Ja'
                  : 'Endnu ikke'
          }
          secondary={
            isExtra
              ? 'Ingen krav til at bestå — ekstra øvelser tæller ikke i progressionen.'
              : proofPending || proofFailed
                ? 'Bestå trin-beviset (95% nøjagtighed på 35 sek) for at gå videre.'
                : cleared
                  ? 'Du nåede 95% nøjagtighed og gennemførte alle runder.'
                  : result.passedAllRounds
                    ? '95% nøjagtighed kræves for at gå videre.'
                    : 'Gennemfør alle runder uden at afbryde med Escape.'
          }
        />
        <InsightCard
          label="Svageste tast"
          primary={
            weakest
              ? weakest.key === ' '
                ? '␣ mellemrum'
                : weakest.key.toUpperCase()
              : 'Ingen'
          }
          secondary={
            weakest ? `${weakest.count} fejl denne lektion` : 'Pænt arbejde.'
          }
        />
        <InsightCard
          label="Langsomste tast"
          primary={
            slowest
              ? slowest.key === ' '
                ? '␣ mellemrum'
                : slowest.key.toUpperCase()
              : 'Ingen'
          }
          secondary={
            slowest
              ? `Ca. ${Math.round(slowest.avgMs)} ms reaktionstid`
              : 'Reaktionstider ligner hinanden.'
          }
        />
      </section>

      <section className="surface p-5">
        <div className="flex items-center justify-between">
          <h2 className="label">Belønning</h2>
          <span className="font-mono text-2xl text-amber-600 dark:text-amber-300">
            +{xpEarned} XP
          </span>
        </div>
        {newBadges.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {newBadges.map((b) => (
              <span
                key={b.id}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-100 px-3 py-1.5 ring-1 ring-amber-200 text-amber-900 dark:bg-amber-500/10 dark:ring-amber-500/30 dark:text-amber-200 animate-fade-in-up"
              >
                <span aria-hidden="true">★</span>
                <span className="text-sm">{b.name}</span>
              </span>
            ))}
          </div>
        )}
      </section>

      <section className="surface p-5">
        <h2 className="label">Anbefalet næste lektion</h2>
        <p className="mt-2 text-strong">{nextRecommendation(result)}</p>
      </section>

      <div className="flex flex-col gap-2 sm:flex-row">
        {(proofFailed || proofPending) && onRetryProof && (
          <button
            type="button"
            onClick={onRetryProof}
            className="flex-1 rounded-xl bg-amber-500 px-4 py-3 font-semibold text-white shadow-sm transition hover:bg-amber-400"
          >
            {proofFailed ? 'Gentag trin-bevis' : 'Start trin-bevis'}
          </button>
        )}
        <button
          onClick={onRepeat}
          className={
            !isExtra && rec.kind === 'repeat' && !proofFailed && !proofPending
              ? 'flex-1 rounded-xl bg-indigo-500 px-4 py-3 font-semibold text-white shadow-sm transition hover:bg-indigo-400 dark:shadow-lg dark:shadow-indigo-500/30'
              : 'flex-1 btn-secondary py-3 font-semibold'
          }
        >
          {isExtra ? `Ny ${extraTitle(result.trinId).toLowerCase()}` : 'Gentag trin'}
        </button>
        {!isExtra && (
          <button
            onClick={onAdvance}
            disabled={!canAdvance}
            className={
              !canAdvance
                ? 'flex-1 cursor-not-allowed rounded-xl bg-zinc-100 px-4 py-3 font-semibold text-zinc-400 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:text-zinc-600 dark:ring-white/5'
                : rec.kind === 'advance'
                  ? 'flex-1 rounded-xl bg-indigo-500 px-4 py-3 font-semibold text-white shadow-sm transition hover:bg-indigo-400 dark:shadow-lg dark:shadow-indigo-500/30'
                  : 'flex-1 btn-secondary py-3 font-semibold'
            }
          >
            {nextTrin
              ? nextTrin.tierId !== result.tierId
                ? `Start ${getTier(nextTrin.tierId)?.title ?? 'næste niveau'}`
                : 'Start næste trin'
              : 'Næste trin'}
          </button>
        )}
        <button onClick={onBack} className="flex-1 btn-secondary py-3 font-semibold">
          Tilbage til oversigt
        </button>
      </div>
      <p className="text-center text-xs text-faint">
        Enter:{' '}
        {proofFailed && onRetryProof
          ? 'gentag trin-bevis'
          : isExtra
            ? 'gentag øvelse'
            : canAdvance
              ? 'næste trin'
              : 'gentag trin'}
      </p>
    </div>
  );
}

function extraTitle(trinId: string): string {
  switch (trinId) {
    case WARMUP_TRIN_ID:
      return 'Daglig varm-op';
    case FLOW_PRACTICE_TRIN_ID:
      return 'Træningsbane';
    case METRONOME_TRIN_ID:
      return 'Rytme-runde';
    case SHADOW_TRIN_ID:
      return 'Skygge-runde';
    case DICTATION_TRIN_ID:
      return 'Orddiktat';
    case STORY_TRIN_ID:
      return 'Fortælling';
    case STORY2_TRIN_ID:
      return 'Fortælling 2';
    case WEAK_SPOTS_TRIN_ID:
      return 'Svage punkter';
    case PUNCTUATION_PRACTICE_TRIN_ID:
      return 'Øverunde: Komma og punktum';
    case CUSTOM_PRACTICE_TRIN_ID:
      return 'Vælg selv';
    case BONUS_TAL_TRIN_ID:
      return 'Bonus: Tal';
    case BONUS_EMAIL_TRIN_ID:
      return 'Bonus: @ og e-mail';
    case MENTAL_IMAGERY_TRIN_ID:
      return 'Imaginær skrivning';
    default:
      return 'Ekstra øvelse';
  }
}

function nextRecommendation(result: LessonResult): string {
  if (result.trinId === WARMUP_TRIN_ID) {
    return 'Tilbage til oversigt, eller tag den igen i morgen — varm-op virker bedst dagligt.';
  }
  if (result.trinId === FLOW_PRACTICE_TRIN_ID) {
    const mins = formatDuration(result.durationMs);
    return `Du skrev i ${mins} — ${result.correctChars} tegn. Tilbage til oversigt, eller tag et nyt stræk.`;
  }
  if (isExtraTrinId(result.trinId)) {
    return 'Tilbage til oversigt, eller tag en runde til. Ekstra øvelser tæller ikke i progression.';
  }
  const cleared = isTrinCleared(result);
  const trin = getTrin(result.trinId);
  const next = getNextTrin(result.trinId);

  if (cleared && next) {
    if (next.tierId !== result.tierId) {
      const tier = getTier(next.tierId);
      return tier
        ? `${tier.title} er låst op. Start med ${trinLabel(next)}: ${next.title}.`
        : `Fortsæt med ${trinLabel(next)}: ${next.title}.`;
    }
    return `${trinLabel(next)}: ${next.title}. ${next.goal}`;
  }

  const weakest = topWeakKeys(result.errorsByKey, 1)[0];
  if (weakest && weakest.count >= 3) {
    const k = weakest.key === ' ' ? 'mellemrum' : weakest.key.toUpperCase();
    return trin
      ? `${trinLabel(trin)} igen — med ekstra fokus på ${k}.`
      : `Gentag trin med fokus på ${k}.`;
  }

  return trin
    ? `${trinLabel(trin)} igen — samme runder, mere flow.`
    : 'Gentag lektionen for mere flow.';
}

function BigStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface p-4">
      <p className="label">{label}</p>
      <p className="mt-1 font-mono text-3xl text-strong">{value}</p>
    </div>
  );
}

function InsightCard({
  label,
  primary,
  secondary,
}: {
  label: string;
  primary: string;
  secondary: string;
}) {
  return (
    <div className="surface p-4">
      <p className="label">{label}</p>
      <p className="mt-1 text-lg font-semibold text-strong">{primary}</p>
      <p className="mt-1 text-xs text-muted">{secondary}</p>
    </div>
  );
}

function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
